/**
 * fa_17 — Feeding Questionnaire (14th Ave new-student intake) parent fields.
 *
 * Information-gathering only: no signature, no wet-ink, no consent. Submit is
 * gated on (a) a completion-acknowledgment checkbox rendered by the page, and
 * (b) every Yes/No question answered. Narratives and the drinking/eating
 * activity checkboxes are all optional.
 *
 * The 13 Yes/No feeding-behavior questions are listed in
 * parentPacketValidation.FA17_REQUIRED_YESNO_KEYS.
 */
import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { packetFieldId } from './parentPacketValidation';

const FEEDING_YN = [
  { key: 'feeding_refuses_food', label: 'My child refuses food.' },
  { key: 'feeding_vomits_after', label: 'My child vomits after feeding.' },
  { key: 'feeding_vomits_during', label: 'My child vomits during feeding.' },
  { key: 'feeding_takes_long', label: 'My child takes a long time to eat.' },
  { key: 'feeding_difficulty_liquids', label: 'My child has difficulty drinking liquids.' },
  { key: 'feeding_difficulty_solids', label: 'My child has difficulty eating solids.' },
  { key: 'feeding_food_spills', label: "Food spills out of my child's mouth." },
  { key: 'feeding_cries_during', label: 'My child cries during mealtime.' },
  { key: 'feeding_cries_after', label: 'My child cries after mealtime.' },
  { key: 'feeding_needs_force_feeding', label: 'My child needs to be force-fed.' },
  { key: 'feeding_drools_frequently', label: 'My child drools frequently.' },
  { key: 'feeding_coughs_when_eating', label: 'My child coughs when eating.' },
  { key: 'feeding_chokes_when_eating', label: 'My child chokes when eating.' },
];

const DRINKING = [
  { key: 'drink_bottle', label: 'Bottle' },
  { key: 'drink_cup', label: 'Cup' },
  { key: 'drink_straw', label: 'Straw' },
  { key: 'drink_nurses', label: 'Nurses' },
  { key: 'drink_other', label: 'Other' },
];

const EATING = [
  { key: 'eat_fingers', label: 'Uses fingers' },
  { key: 'eat_spoon', label: 'Spoon' },
  { key: 'eat_fork', label: 'Fork' },
  { key: 'eat_chews_solid', label: 'Chews solid food' },
  { key: 'eat_assisted', label: 'Eats assisted' },
  { key: 'eat_unassisted', label: 'Eats unassisted' },
];

function YesNoPair({
  label, fieldKey, value, onChange, error,
}) {
  const set = (next) => onChange(value === next ? '' : next);
  return (
    <Box
      id={packetFieldId(fieldKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        flexWrap: 'wrap',
        ...(error
          ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, my: 0.25 }
          : {}),
      }}
    >
      <Typography variant="body2" sx={{ flex: '1 1 60%', minWidth: 220 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={value === 'yes'}
              onChange={() => set('yes')}
              color={error ? 'error' : 'primary'}
            />
          )}
          label="Yes"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={value === 'no'}
              onChange={() => set('no')}
              color={error ? 'error' : 'primary'}
            />
          )}
          label="No"
        />
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ width: '100%' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

function SectionHeader({ roman, title }) {
  return (
    <Box sx={{ mt: 3, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {roman}. {title}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function Fa17FeedingQuestionnaireFields({
  fields, setF, fieldErrors = {}, clearFieldError,
}) {
  const onText = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.value);
  };
  const onCheck = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.checked);
  };
  const setYn = (key) => (next) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, next);
  };

  const text = (key) => fields[key] ?? '';

  const narrative = (label, key, helperText) => (
    <TextField
      key={key}
      id={packetFieldId(key)}
      fullWidth
      size="small"
      margin="dense"
      label={label}
      value={text(key)}
      onChange={onText(key)}
      multiline
      minRows={3}
      helperText={helperText}
    />
  );

  const short = (label, key) => (
    <TextField
      key={key}
      id={packetFieldId(key)}
      fullWidth
      size="small"
      margin="dense"
      label={label}
      value={text(key)}
      onChange={onText(key)}
    />
  );

  const checkbox = (label, key) => (
    <FormControlLabel
      key={key}
      control={(
        <Checkbox
          size="small"
          checked={!!fields[key]}
          onChange={onCheck(key)}
        />
      )}
      label={label}
    />
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Please tell us about how your child eats and drinks. You can leave
        narrative answers blank if nothing comes to mind, but every Yes/No
        question must be answered.
      </Typography>

      {short('Person completing this form', 'person_completing_form')}

      <SectionHeader roman="I" title="Feeding Behavior" />
      {narrative(
        'What types of difficulties do you experience while feeding your child?',
        'feeding_difficulties_narrative',
      )}
      <Box sx={{ mt: 1 }}>
        {FEEDING_YN.map((q) => (
          <YesNoPair
            key={q.key}
            label={q.label}
            fieldKey={q.key}
            value={text(q.key)}
            onChange={setYn(q.key)}
            error={fieldErrors[q.key]}
          />
        ))}
      </Box>
      {narrative('Comments', 'feeding_comments')}

      <SectionHeader roman="II" title="Feeding Activities" />
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Drinking</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Check all that apply.
      </Typography>
      <Grid container>
        {DRINKING.map((d) => (
          <Grid item xs={6} sm={4} key={d.key}>
            {checkbox(d.label, d.key)}
          </Grid>
        ))}
      </Grid>
      {fields.drink_other && short('Describe the other way your child drinks', 'drink_other_text')}

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Eating</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Check all that apply.
      </Typography>
      <Grid container>
        {EATING.map((e) => (
          <Grid item xs={6} sm={4} key={e.key}>
            {checkbox(e.label, e.key)}
          </Grid>
        ))}
      </Grid>

      <SectionHeader roman="III" title="Food" />
      {narrative('What foods is your child allergic to?', 'food_allergic_to')}
      {narrative('What foods does your child dislike?', 'food_dislikes')}
      {narrative('What foods does your child prefer?', 'food_prefers')}
      {narrative('What foods is your child restricted from?', 'food_restricted_from')}
      {narrative(
        'What foods can your child eat, and at what consistency?',
        'food_can_eat_consistency',
        'For example: liquids, pureed, mashed, chopped, or pieces. Tell us what works.',
      )}
      {narrative('Which foods are easiest for your child to eat?', 'food_easiest')}
      {narrative('Which foods are hardest for your child to eat?', 'food_hardest')}
      {narrative('Comments', 'food_comments')}
    </Box>
  );
}

/** Default empty fields for fa_17 — every required Y/N starts unanswered (''). */
export function initialFa17Fields() {
  const init = {};
  const RYN = [
    'feeding_refuses_food',
    'feeding_vomits_after',
    'feeding_vomits_during',
    'feeding_takes_long',
    'feeding_difficulty_liquids',
    'feeding_difficulty_solids',
    'feeding_food_spills',
    'feeding_cries_during',
    'feeding_cries_after',
    'feeding_needs_force_feeding',
    'feeding_drools_frequently',
    'feeding_coughs_when_eating',
    'feeding_chokes_when_eating',
  ];
  for (const k of RYN) init[k] = '';
  return init;
}
